import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  getOrganizationPredictionsSettings,
  getOrganizationSuggestionsSettings,
  patchOrganizationPredictionsSettings,
  patchOrganizationSuggestionsSettings,
} from 'src/queries/organizations/automationSettings/automationSettings.network'
import {
  OrganizationPredictionsSettingsResponse,
  OrganizationSuggestionsSettingsResponse,
  PatchOrganizationPredictionsSettingsRequest,
  PatchOrganizationSuggestionsSettingsRequest,
} from 'src/queries/organizations/automationSettings/automationSettings.types'
import { orgQueryKeys } from 'src/queries/queries.constants'
import {
  CustomMutationOptions,
  CustomQueryOptions,
} from 'src/queries/queries.utils'
import { selectIsBetaOrgSuggestionsAndPredictions } from 'src/redux/selectors/data/organizations.selectors'

export function useQueryOrganizationSuggestionsSettings(
  orgId: string | undefined,
  options?: CustomQueryOptions<OrganizationSuggestionsSettingsResponse>,
) {
  return useQuery({
    queryKey: orgQueryKeys.organizationSuggestions(orgId),
    queryFn: () => getOrganizationSuggestionsSettings(orgId!),
    enabled: Boolean(orgId),
    ...options,
  })
}

export function useQueryOrganizationPredictionsSettings(
  orgId: string | undefined,
  options?: CustomQueryOptions<OrganizationPredictionsSettingsResponse>,
) {
  return useQuery({
    queryKey: orgQueryKeys.organizationPredictions(orgId),
    queryFn: () => getOrganizationPredictionsSettings(orgId!),
    enabled: Boolean(orgId),
    ...options,
  })
}

type UpdateSuggestionsParams = {
  orgId: string
  request: PatchOrganizationSuggestionsSettingsRequest
}

export function usePatchOrganizationSuggestionsSettings(
  options?: CustomMutationOptions<
    OrganizationSuggestionsSettingsResponse,
    Error,
    UpdateSuggestionsParams
  >,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: UpdateSuggestionsParams) =>
      patchOrganizationSuggestionsSettings(params.orgId, params.request),
    onSuccess: (_data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: orgQueryKeys.organizationSuggestions(variables.orgId),
      })
      options?.onSuccess?.(_data, variables, context)
    },
    ...options,
  })
}

type UpdatePredictionsParams = {
  orgId: string
  request: PatchOrganizationPredictionsSettingsRequest
}

export function usePatchOrganizationPredictionsSettings(
  options?: CustomMutationOptions<
    OrganizationPredictionsSettingsResponse,
    Error,
    UpdatePredictionsParams
  >,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: UpdatePredictionsParams) =>
      patchOrganizationPredictionsSettings(params.orgId, params.request),
    onSuccess: (_data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: orgQueryKeys.organizationPredictions(variables.orgId),
      })
      options?.onSuccess?.(_data, variables, context)
    },
    ...options,
  })
}

export function useQueryCanUseAIExpenseCategories() {
  const isBetaOrgSuggestionsAndPredictions = useSelector(
    selectIsBetaOrgSuggestionsAndPredictions,
  )
  const {
    data: saasEnabledFeatures,
    isLoading: isSaasEnabledLoading,
    error: saasEnabledError,
  } = useQueryUserFeatures([UserFeature.SAAS_ENABLED])
  const {
    data: aiExpCatsFeatures,
    isLoading: isAIExpCatsLoading,
    error: aiExpCatsError,
  } = useQueryUserFeatures([UserFeature.AI_EXPENSE_CATEGORIES])
  const isSaasEnabled = Boolean(saasEnabledFeatures?.SAAS_ENABLED || false)
  const hasAIExpenseCategoriesFeature = Boolean(
    aiExpCatsFeatures?.AI_EXPENSE_CATEGORIES || false,
  )
  return {
    data: Boolean(
      isBetaOrgSuggestionsAndPredictions &&
        (hasAIExpenseCategoriesFeature || !isSaasEnabled),
    ),
    isLoading: isSaasEnabledLoading || isAIExpCatsLoading,
    error: saasEnabledError || aiExpCatsError,
  }
}
